import {
  STORE_KEY_ID_LIST,
  evalCondition,
  storeGet,
} from 'mailup-react-helpers'

import {
  beRecipientApiGetClusterCounter,
  beRecipientApiGetEmailListSubscriptionCounters,
  beRecipientApiGetSmsListSubscriptionCounters,
  beRecipientApiGetUnsubscriptionGroupedByCounter,
  beRecipientApiHasRecipients,
} from 'Api/beRecipientApiTS'

import getIntl from 'Helpers/intl'
import type {
  ChartItemData,
  ChartOptoutType,
} from 'Pages/RecipientsOverview/types'

const chartColor = {
  Axis: '#D5DADF',
  Purple: '#9D74DF',
  LightblueZero: '#00A6CF',
  LightblueZeroFill: '#cff1f9',
  LightblueOne: '#256A7C',
  LightblueOne2: '#228eab',
  LightblueTwo: '#1E9DBE',
  LightblueThree: '#69CBE5',
  LightblueFour: '#98edc9',
  GialloDark: '#FFC31A',
  AcquamarineOne: '#39BA7C',
  AcquamarineOneFill: '#CFF8E4',
  AcquamarineTwoLight: '#98EDC9',
  AcquamarineTwo: '#8ACF61',
  AcquamarineThree: '#B0E2C9',
  Grey: '#DDDDDD',
  GreyDark: '#5D5D5D',
  YellowTwo: '#FFD356',
  YellowThree: '#EEA236',
  RedThree: '#FD7F75',
  DarkThree: '#CBCBCB',
  GreenOne: '#4ac184',
  GreenTwo: '#8ACF61',
  BlueChill: '#1286A4',
  AthensGray: '#E6E9EC',
  Mustard: '#FFD754',
  Emerald: '#4bc88c',
  VividTangerine: '#FF8D84',
  //
  EngagementActive: '#AFE390',
  Subscribers: '#a6e2f3',
}

export const getChartOptoutLabels = (): Record<ChartOptoutType, string> => {
  const intl = getIntl()
  return {
    Console: intl.formatMessage({
      id: 'Load.SearchRecipientsUnsubscribed.ChartAdminImport',
    }),
    Oblio: intl.formatMessage({
      id: 'Load.SearchRecipientsUnsubscribed.ChartDirittoOblio',
    }),
    Unsubscribe: intl.formatMessage({
      id: 'Load.SearchRecipientsUnsubscribed.ChartUnsubLink',
    }),
    BounceHB: intl.formatMessage({
      id: 'Load.SearchRecipientsUnsubscribed.ChartEmaailErrata',
    }),
    FBL: intl.formatMessage({
      id: 'Load.SearchRecipientsUnsubscribed.ChartSegnalazioneAbuso',
    }),
    Inactive: intl.formatMessage({
      id: 'Load.SearchRecipientsUnsubscribed.ChartInactiveRecipient',
    }),
    MailBlocked: intl.formatMessage({
      id: 'Load.SearchRecipientsUnsubscribed.ChartEmailBloccata',
    }),
    NBH: intl.formatMessage({
      id: 'Load.SearchRecipientsUnsubscribed.ChartBounces',
    }),
    Undefined: intl.formatMessage({
      id: 'Load.SearchRecipientsUnsubscribed.ChartNotAvaiable',
    }),
  }
}

const chartOptoutOrder: Record<ChartOptoutType, number> = {
  Console: 0,
  Oblio: 1,
  Unsubscribe: 2,
  BounceHB: 3,
  FBL: 4,
  Inactive: 5,
  MailBlocked: 6,
  NBH: 7,
  Undefined: 8,
}

const chartOptoutColors: Record<ChartOptoutType, string> = {
  Console: chartColor.LightblueThree,
  Oblio: chartColor.GreyDark,
  Unsubscribe: chartColor.AcquamarineOne,
  BounceHB: chartColor.DarkThree,
  FBL: chartColor.Purple,
  Inactive: chartColor.YellowThree,
  MailBlocked: chartColor.YellowTwo,
  NBH: chartColor.LightblueTwo,
  Undefined: chartColor.RedThree,
}

export const chartOptoutSort = (a: ChartItemData, b: ChartItemData): number =>
  chartOptoutOrder[a.x as ChartOptoutType] -
  chartOptoutOrder[b.x as ChartOptoutType]

export const fetchEmptyState = async (): Promise<boolean> => {
  const idList = storeGet(STORE_KEY_ID_LIST)

  const hasRecipients = await beRecipientApiHasRecipients(idList)

  return !hasRecipients
}

export const getOptoutData = async (): Promise<ChartItemData[]> => {
  const idList = storeGet(STORE_KEY_ID_LIST)

  const data = await beRecipientApiGetUnsubscriptionGroupedByCounter(idList)

  const optoutLabels = getChartOptoutLabels()

  const optoutTypes: ChartOptoutType[] = [
    'Oblio',
    'BounceHB',
    'FBL',
    'Inactive',
    'MailBlocked',
    'Undefined',
    'NBH',
  ]

  const result: ChartItemData[] = optoutTypes.map((o) => {
    const found = data.find(({ Type }) => Type === o)
    return {
      x: o,
      label: optoutLabels[o],
      value: found !== undefined ? found.Count : 0,
      color: chartOptoutColors[o],
    }
  })

  const unsubscribeValue: ChartItemData = data
    .filter((r) =>
      ['NewsletterLink', 'UnsubMailTo', 'UnsubHttp'].includes(r.Type),
    )
    .reduce((acc, obj) => ({ ...acc, value: acc.value + obj.Count }), {
      x: 'Unsubscribe',
      label: optoutLabels.Unsubscribe,
      value: 0,
      color: chartOptoutColors.Unsubscribe,
    })

  const consoleValue: ChartItemData = data
    .filter((r) => ['Import', 'Platform'].includes(r.Type))
    .reduce((acc, obj) => ({ ...acc, value: acc.value + obj.Count }), {
      x: 'Console',
      label: optoutLabels.Console,
      value: 0,
      color: chartOptoutColors.Console,
    })

  return [
    consoleValue,
    result.find(({ x }) => x === 'Oblio') ?? {
      x: 'Oblio',
      label: optoutLabels.Oblio,
      value: 0,
      color: chartOptoutColors.Oblio,
    },
    unsubscribeValue,
    ...result
      .filter(({ x }) => x !== 'Oblio')
      .map((o) => ({
        ...o,
        label: optoutLabels[o.x as ChartOptoutType],
        color: chartOptoutColors[o.x as ChartOptoutType],
      })),
  ].sort(chartOptoutSort)
}

export const getEmailSubscriptionsData = async (): Promise<ChartItemData[]> => {
  const idList = storeGet(STORE_KEY_ID_LIST)
  const intl = getIntl()

  const emailSubscriptions =
    await beRecipientApiGetEmailListSubscriptionCounters(idList)

  return [
    {
      x: 'optin',
      label: intl.formatMessage({
        id: 'Load.Common.SubscriberPluralUppercase',
      }),
      value: emailSubscriptions.OptInCount,
      color: chartColor.Subscribers,
    },
    {
      x: 'pending',
      label: intl.formatMessage({
        id: 'Load.MailupCommon.Pending',
      }),
      value: emailSubscriptions.PendingCount,
      color: chartColor.Mustard,
    },
    {
      x: 'optout',
      label: intl.formatMessage({
        id: 'Load.MailupCommon.Unsubscribed',
      }),
      value: emailSubscriptions.OptOutCount,
      color: chartColor.VividTangerine,
    },
  ]
}

export const getSmsSubscriptionsData = async (): Promise<ChartItemData[]> => {
  const idList = storeGet(STORE_KEY_ID_LIST)
  const intl = getIntl()

  const smsSubscriptions =
    await beRecipientApiGetSmsListSubscriptionCounters(idList)

  return [
    {
      x: 'optin',
      label: intl.formatMessage({
        id: 'Load.Common.SubscriberPluralUppercase',
      }),
      value: smsSubscriptions.OptInCount,
      color: chartColor.Subscribers,
    },
    {
      x: 'pending',
      label: intl.formatMessage({
        id: 'Load.MailupCommon.Pending',
      }),
      value: smsSubscriptions.PendingCount,
      color: chartColor.Mustard,
    },
    {
      x: 'optout',
      label: intl.formatMessage({
        id: 'Load.MailupCommon.Unsubscribed',
      }),
      value: smsSubscriptions.OptOutCount,
      color: chartColor.VividTangerine,
    },
  ]
}

export const getEngagementChartData = async (): Promise<{
  data: ChartItemData[]
  upselling: boolean
}> => {
  const idList = storeGet(STORE_KEY_ID_LIST)
  const hasEngagement: boolean = evalCondition('co:DELSTATS OR co:ENGAGEMENT')

  if (hasEngagement) {
    const intl = getIntl()
    const clusters = await beRecipientApiGetClusterCounter(idList, [
      'Subscribed',
    ])

    return {
      upselling: false,
      data: [
        {
          x: 'VeryActive',
          label: intl.formatMessage({ id: 'Load.MailupCommon.VeryActive' }),
          value: clusters.find((o) => o.Cluster === 5)?.Count ?? 0,
          color: chartColor.Emerald,
        },
        {
          x: 'Active',
          label: intl.formatMessage({ id: 'Load.MailupCommon.ActivePlural' }),
          value: clusters.find((o) => o.Cluster === 4)?.Count ?? 0,
          color: chartColor.EngagementActive,
        },
        {
          x: 'LessActive',
          label: intl.formatMessage({ id: 'Load.MailupCommon.LessActive' }),
          value: clusters.find((o) => o.Cluster === 3)?.Count ?? 0,
          color: chartColor.Mustard,
        },
        {
          x: 'Inactive',
          label: intl.formatMessage({ id: 'Load.Common.Inactive' }),
          value: clusters.find((o) => o.Cluster === 2)?.Count ?? 0,
          color: chartColor.AthensGray,
        },
        {
          x: 'New',
          label: intl.formatMessage({ id: 'Load.MailupCommon.NewPlural' }),
          value: clusters.find((o) => o.Cluster === 1)?.Count ?? 0,
          color: chartColor.BlueChill,
        },
        {
          x: 'Excluded',
          label: intl.formatMessage({
            id: 'Load.RecipientsOverview.EngagementClusterExcluded',
          }),
          value: clusters.find((o) => o.Cluster === 6)?.Count ?? 0,
          color: chartColor.Purple,
        },
      ],
    }
  } else {
    return {
      upselling: true,
      data: [],
    }
  }
}

/*
export const fetchRecipientsData = async (): Promise<
  [ChartItemData[], ChartItemData[], ChartItemData[], EngagementChartState]
> => {
  return await Promise.all([
    getEmailSubscriptionsData(),
    getSmsSubscriptionsData(),
    fetchOptoutData(),
    fetchEngagementChartData()
  ])
}
*/
